import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';
import { IFilters } from '../TableFilters/useFilters';
import { StatsType } from '../../config/stats';
import { ITableMatchItem } from '../../models/Match';
import { useHistoryStats } from '../../hooks/useHistoryStats';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fab from '@material-ui/core/Fab';
import ArrowLeft from '@material-ui/icons/ArrowLeft';
import IconButton from '@material-ui/core/IconButton';
import { HistoricalMatch } from '../../models/HistoricalStats';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { getStatsDetails, isStatPassCondition } from '../../utils/stats';
import { useAppContext } from '../../hooks/useAppContext';
import { StatInfo } from '../../models/Stats';
import { ErrorBoundary } from 'react-error-boundary';
import * as S from './TableItemModalStyles';
import { TransformedEvents } from '../../models/MatchMongo';
import { isLateGoalBaseOnTime } from '../../utils/lateGoals';
import { isEmpty } from 'lodash';
import { LATE_GOAL_CONFIG } from '../../config/late';

interface TableItemModalProps {
  open: boolean;
  handleClose: () => void;
  match: ITableMatchItem;
  date: IFilters['date'];
  statName: StatsType;
}

interface HistoryMatchesProps {
  title: string;
  matches: HistoricalMatch[];
  team?: string;
  statName: StatsType;
}
const isBreaker = (statName: StatsType) => {
  const [type] = getStatsDetails(statName);
  return type === 'breaker';
};

const isLate = (statName: StatsType) => {
  const [type, stats] = getStatsDetails(statName);
  return type === 'goal' && stats.toLocaleLowerCase().includes('late');
};
const shouldShowTeamsColumn = (statName: StatsType) => {
  return isLate(statName) || isBreaker(statName);
};
export const TableItemModal = ({ open, handleClose, match, statName, date }: TableItemModalProps) => {
  const { awayId, homeId } = match.payload;
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const { isLoading, isError, matches } = useHistoryStats({
    date,
    statName,
    homeTeamId: homeId,
    awayTeamId: awayId,
  });
  const createLastLabel = (statName: StatsType) => {
    if (isLate(statName)) return 'Goal Minutes';
    if (isBreaker(statName)) return 'Score';

    return 'All';
  };
  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <S.Text>Ładowanie...</S.Text>
          <S.SpinnerContainer>
            <CircularProgress />
          </S.SpinnerContainer>
        </>
      );
    }

    if (isError) {
      return <S.Text>Wystąpił błąd spróbuj ponownie załadować dane</S.Text>;
    }

    const { data, statInfo } = matches;
    const statNameCapitalized = statInfo?.type.charAt(0).toUpperCase() + statInfo?.type.slice(1);
    const isTeamColumnHide = shouldShowTeamsColumn(statName);

    return (
      <ErrorBoundary fallback={<div>Wystąpił problem z ładowaniem meczy historycznych</div>}>
        <S.Container>
          {/* <Content> */}
          <S.HeaderHistoryMatchContainer isTeamColumnHide={isTeamColumnHide} isLate={isLate(statName)}>
            <span>Date</span>
            <span>Match</span>
            {!isTeamColumnHide ? <span>{statNameCapitalized} per team</span> : null}
            <span>{createLastLabel(statName)}</span>
          </S.HeaderHistoryMatchContainer>
          <HistoryMatches statName={statName} title={`Mecze: ${match.homeTeam}`} matches={data.homeTeamMatches} team={match.homeTeam} />
          <HistoryMatches statName={statName} title={`Mecze: ${match.awayTeam}`} matches={data.awayTeamMatches} team={match.awayTeam} />
          <HistoryMatches statName={statName} title={`Mecze H2H`} matches={data.mutualMatches} />
          {/* </Content> */}
        </S.Container>
      </ErrorBoundary>
    );
  };

  return (
    <Dialog
      aria-labelledby="dialog-with-match-stats"
      open={open}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <S.Header id="max-width-dialog-title">
        <Fab aria-label="Wróc do listy meczy" color="secondary" size="small" onClick={handleClose}>
          <ArrowLeft fontSize="large" />
        </Fab>
        <S.CenterText>{match.match}</S.CenterText>
        <IconButton size="small">
          <S.StyledStarBorderIcon fontSize="large" />
        </IconButton>
      </S.Header>
      <S.MatchInfo>
        {match.fullDate} - {match.country} : {match.league}
      </S.MatchInfo>
      {helperLateGoalText(statName)}
      {renderContent()}
    </Dialog>
  );
};

const helperLateGoalText = (statName: StatsType) => {
  if (!isLate(statName)) return null;

  return (
    <S.StatHelperText>
      Gole late liczymy od {LATE_GOAL_CONFIG.htStart}' pierwszej połowy i od {LATE_GOAL_CONFIG.ftStart}' drugiej połowy
    </S.StatHelperText>
  );
};

const getMinutesFromMatchEvent = (event: TransformedEvents) => {
  const { elapsed, extra } = event.time;

  let time = `${elapsed}`;
  if (extra) {
    time += `+${extra}`;
  }
  time += `'`;

  return <S.StyledLateMinutes isPassed={isLateGoalBaseOnTime(elapsed)}>{time}</S.StyledLateMinutes>;
};

const fallbackForEmptyGoals = (minutes: JSX.Element[]) => {
  if (isEmpty(minutes)) return '-';

  return minutes;
};
const showResult = (stats: HistoricalMatch['stats'], isTeamColumnHide: boolean) => {
  if (stats.statType === 'late') {
    const homeMinutes = stats.home.map(getMinutesFromMatchEvent);
    const awayMinutes = stats.away.map(getMinutesFromMatchEvent);

    return (
      <S.FlexColumn>
        <div>{fallbackForEmptyGoals(homeMinutes)}</div>
        <div>{fallbackForEmptyGoals(awayMinutes)}</div>
      </S.FlexColumn>
    );
  }

  return (
    <>
      {!isTeamColumnHide ? (
        <S.FlexColumn>
          <span>{stats.home}</span>
          <span>{stats.away}</span>
        </S.FlexColumn>
      ) : null}
      <div>
        <S.StyledScore>{stats.textValue}</S.StyledScore>
      </div>
    </>
  );
};
const HistoryMatches = ({ matches, title, team, statName }: HistoryMatchesProps) => {
  const { selectedStatsCondition: statsCondition } = useAppContext();
  const isTeamColumnHide = shouldShowTeamsColumn(statName);

  return (
    <ErrorBoundary fallback={<div>Wystąpił problem z ładowaniem meczy</div>}>
      <div>
        <S.Text>
          {title} ({matches.length})
        </S.Text>

        {matches?.map((match) => {
          return (
            <S.HistoryMatchContainer
              key={match.id}
              shouldHighlight={Boolean(statsCondition)}
              isStatsPass={isStatPassCondition({ matchStats: match.stats, statsCondition, statName })}
              isTeamColumnHide={isTeamColumnHide}
              isLate={isLate(statName)}
            >
              <span>{match.fullDate}</span>
              <S.FlexColumn>
                <S.Team isFirst isHighlighted={match.homeTeam === team}>
                  {match.homeTeam}
                </S.Team>
                <S.Team isHighlighted={match.awayTeam === team}>{match.awayTeam}</S.Team>
              </S.FlexColumn>
              {showResult(match.stats, isTeamColumnHide)}
            </S.HistoryMatchContainer>
          );
        })}
      </div>
    </ErrorBoundary>
  );
};

const Transition = React.forwardRef(function Transition(props: TransitionProps & { children?: React.ReactElement<any, any> }, ref: React.Ref<unknown>) {
  return <Slide direction="up" ref={ref} {...props} />;
});
